/**
 * A FELTÖLTÉS MAGA — a §8c 7. lépése a felület felől, csoportos módban.
 *
 * ── EGY KÖTEG EGYSZERRE, ÉS EZ SZÁNDÉKOS ─────────────────────────────
 *
 * Az ügyfél telefonon áll, mobilhálózaton. Több párhuzamos feltöltés
 * ugyanarról a kapcsolatról nem gyorsabb, viszont a hibaüzenetek
 * összekeverednek: melyik fájl bukott el? A `busy` amíg tart, a további
 * indítást elnyeli — és a részletek is EGYMÁS UTÁN mennek, nem
 * `Promise.all`-lal.
 *
 * ── A RÉSZLETEKRE BONTÁS A `chunking.ts`-É ───────────────────────────
 *
 * A szerver kérésenként 20 fájlt és 25 MiB-ot fogad. Egy nagyobb kiválasztás
 * több kérésre bomlik; a felület felé ez EGY művelet marad, egyetlen
 * nyugtával. A számolás azért külön fájl, hogy fájlok nélkül is tesztelhető
 * legyen.
 *
 * ── A RÉSZLEGES SIKER VALÓDI KIMENET, NEM SZÉLSŐSÉG ──────────────────
 *
 * Öt részletből a harmadik elbukhat úgy, hogy az első kettő már beérkezett.
 * Ezért EGY lezáró állapot van (`settled`), és az egyszerre hordozza, hogy
 * mennyi ment át ÉS hogy mi bukott el. Ha a hiba felülírná a számokat, az
 * ügyfél azt hinné, semmi nem érkezett meg — és újraküldené az egészet.
 * (A `2.6/20` ezt kivédi az adatbázisban, de a fölösleges kör akkor is a
 * mobilnetjét eszi, és a bizonytalanság megmarad.)
 *
 * ── A „MÁR MEGKAPTUK" NEM HIBAÁG ─────────────────────────────────────
 *
 * A `2.6/20` elfogadási feltétele éppen ez: ugyanaz a fájl kétszer feltöltve
 * EGY bizonylat. Telefonon a megszakadt feltöltés újrapróbálása a NORMÁLIS
 * viselkedés, nem felhasználói hiba — a felület ezért nyugtáz („ezt már
 * megkaptuk"), nem panaszkodik. Kötegben ez a gyakoribb eset, nem a ritkább:
 * aki egy mappát tölt fel másodszor, annak a nagy része már bent van.
 *
 * ── A HIBA A SZERVER SZAVÁVAL SZÓL, HA VAN ───────────────────────────
 *
 * A méret- és a formátum-korlát a szerveren él (`DocumentController`), és
 * magyarul válaszol. A felület a saját szövegét csak akkor teszi oda, ha a
 * szerver nem mondott semmit — így a korlát EGY helyen van karbantartva.
 * A státuszkód szerinti szétválasztás azért kell, mert a `413`-nál a törzs
 * gyakran üres (a proxy vágja el a kérést, mielőtt az app válaszolna).
 *
 * ── A TÍPUS A FELTÖLTÉS UTÁN MEGY FEL, NEM VELE (#85) ────────────────
 *
 * Az ügyfél a „Mi ez?"-re a feltöltés ELŐTT felel; a választást a hívó adja
 * át (`docType`). A szerver a feltöltéskor nem fogad típust, utólag igen
 * (`PATCH /documents/:id/type` — a cég engedélyével, maszkban naplózva). A
 * köteg ÚJ bizonylatai ezért a feltöltés után, egymás után kapják meg.
 *
 * A „MÁR MEGKAPTUK" BIZONYLAT NEM KAPJA MEG. Az ügyfél egy korábban feltöltött
 * fájlt küldött újra; annak típusáról akkor már nyilatkozott, vagy a könyvelő
 * döntött — egy újrapróbált feltöltés ne írja át.
 *
 * A BUKÁS NEM VESZÍT SEMMIT: a bizonylat megvan, csak típus nélkül, és a
 * listában ott marad mellette az utólagos „Mi ez?". De a nyugta KIMONDJA
 * (`undeclared`) — különben az ügyfél azt hinné, a választása rögzült.
 */

import { useCallback, useRef, useState } from 'react';
import { ApiError, upload } from '@/lib/api-client';
import { planChunks, type SkippedFile } from './chunking';
import { declareDocType } from './declareDocType';
import type { ClientDocType, UploadAccepted } from './types';

export interface UploadFailure {
  messageKey: string;
  serverMessage: string | null;
}

export type UploadOutcome =
  | { kind: 'idle' }
  /** A `done`/`total` FÁJLBAN számol, nem részletben — az ügyfél fájlokat lát. */
  | { kind: 'busy'; done: number; total: number }
  | {
      kind: 'settled';
      /** Ennyiből lett új bizonylat. */
      stored: number;
      /** Ennyit már korábban megkaptunk — `2.6/20`. */
      known: number;
      /** El sem indult — túl nagy vagy nem elfogadott típus, okkal együtt. */
      skipped: SkippedFile[];
      /** `null`, ha minden elküldött részlet átment. */
      failure: UploadFailure | null;
      /**
       * Ennyi ÚJ bizonylatnál nem sikerült rögzíteni a választott típust
       * (#85). A bizonylat megvan; a típus a listában utólag megadható.
       */
      undeclared: number;
    };

export interface UploaderState {
  outcome: UploadOutcome;
  busy: boolean;
  /** `docType`: a „Mi ez?" válasza (#85); elhagyva vagy `null`-lal nem írunk típust. */
  send: (files: File[], docType?: ClientDocType | null) => Promise<void>;
  /**
   * EGY BIZONYLAT OLDALAI — a kamerából, a megadott sorrendben.
   *
   * ⚠️ NEM A `send`, ÉS EZ A LÉNYEGE: a `send` méret szerint DARABOL, ami az
   * egyfájlos úton helyes (minden fájlból külön bizonylat lesz, mindegy
   * melyik kérésben), egy oldalsorozatnál viszont csendben elrontaná a
   * bizonylatot. A második darab a szerveren a folytatás-ágra futna, ahol a
   * teljes-oldalkészlet-őr elutasítaná — és az ügyfél egy fél sorozat után
   * kapna hibát, a papírral a kezében.
   *
   * A sorozat tehát EGY kérésben megy, vagy sehogy. Hogy belefér-e, azt a
   * kamera előre méri (`session-limits.ts`).
   */
  sendPages: (pages: Blob[], docType?: ClientDocType | null) => Promise<void>;
  /**
   * A FUTÓ FELTÖLTÉS MEGSZAKÍTÁSA. Nem díszgomb: enélkül egy tévedésből
   * elindított nagy köteg alól nincs kiút, és a felület addig letiltva marad.
   */
  cancel: () => void;
  reset: () => void;
}

/**
 * EGY RÉSZLET KIMENETÉBŐL A HIBA — a három ág egy helyen.
 *
 * A „Mégsem" és a lejárt időkorlát UGYANAZT az `AbortError`-t adja; a
 * különbséget csak mi tudjuk, és a felhasználónak számít (az egyik a mi
 * bukásunk, a másik az ő döntése).
 */
function failureOf(result: ChunkResult, cancelledByUser: boolean): UploadFailure | null {
  if (result.kind === 'accepted') {
    return null;
  }
  const failure =
    result.kind === 'refused'
      ? { messageKey: result.messageKey, serverMessage: result.serverMessage }
      : result.failure;

  return cancelledByUser && failure.messageKey === TIMED_OUT_KEY
    ? { messageKey: CANCELLED_KEY, serverMessage: null }
    : failure;
}

const PAYLOAD_TOO_LARGE = 413;
const UNSUPPORTED_MEDIA_TYPE = 415;

/**
 * A MEGSZAKÍTOTT KÉRÉS KULCSAI. Névvel exportálva, mert a felület MÁSKÉNT
 * mutatja a kettőt: a lejárt időkorlát a MI bukásunk (hangos), a „Mégsem" a
 * felhasználó saját döntése (halk nyugta, nem riasztás).
 */
export const TIMED_OUT_KEY = 'upload.timedOut';
export const CANCELLED_KEY = 'upload.cancelled';

/**
 * ⚠️ A NÉVRE NÉZÜNK, NEM AZ ÖRÖKLÉSLÁNCRA. A megszakítás `DOMException`-t dob,
 * ami a böngészőkben az `Error` leszármazottja, jsdom alatt viszont NEM — egy
 * `instanceof Error` őr ott némán elenged, és a megszakítás „a feltöltés nem
 * sikerült"-ként jelenne meg. Mérve 2026-09-04-én: a teszt bukott rajta, a
 * futó felület viszont nem szólt volna róla soha.
 */
function isAbort(caught: unknown): boolean {
  return (
    typeof caught === 'object' &&
    caught !== null &&
    'name' in caught &&
    (caught as { name: unknown }).name === 'AbortError'
  );
}

function failureFor(caught: unknown): UploadFailure {
  // AZ ABORT NEM HÁLÓZATI HIBA. „A feltöltés nem sikerült, próbáld újra" itt
  // félrevezetne: vagy a felhasználó állította le, vagy az időkorlát járt le,
  // és a teendő mindkettőnél más.
  if (isAbort(caught)) {
    return { messageKey: TIMED_OUT_KEY, serverMessage: null };
  }
  if (!(caught instanceof ApiError)) {
    return { messageKey: 'upload.failed', serverMessage: null };
  }

  const messageKey =
    caught.status === PAYLOAD_TOO_LARGE
      ? 'upload.tooLarge'
      : caught.status === UNSUPPORTED_MEDIA_TYPE
        ? 'upload.wrongType'
        : 'upload.failed';

  return { messageKey, serverMessage: caught.message || null };
}

interface ChunkOutcome {
  stored: number;
  known: number;
  /** Az ÚJ bizonylatok azonosítói — ezek kapják meg a választott típust. */
  newIds: string[];
  /** Elment, de a szerver visszaadta (413/415) — a fájlok névvel. */
  refused: SkippedFile[];
  failure: UploadFailure | null;
}

/**
 * A KÉRÉS IDŐKORLÁTJA, A KÜLDÖTT BÁJTOKHOZ MÉRVE.
 *
 * Fix időkorlát itt nem működik: egy 200 KB-os parkolójegy és egy 25 MiB-os
 * köteg között két nagyságrend van. Az alap a kapcsolatfelvételre és a
 * szerveroldali feldolgozásra megy (a hash a teljes bájtsorozatból képződik),
 * a MiB-enkénti rész magára a feltöltésre. Bőkezűen mérve: egy lassú mobilnet
 * ~0,5 Mbit/s-nél is beleférjen, mert a rossz irányba tévedés ára egy
 * FÉLBEVÁGOTT, sikeres feltöltés.
 */
const TIMEOUT_BASE_MS = 20_000;
const TIMEOUT_PER_MIB_MS = 12_000;

function timeoutFor(chunk: readonly File[]): number {
  const mib = chunk.reduce((sum, file) => sum + file.size, 0) / (1024 * 1024);
  return TIMEOUT_BASE_MS + Math.ceil(mib * TIMEOUT_PER_MIB_MS);
}

/**
 * A RÉSZLETEK ELKÜLDÉSE, EGYMÁS UTÁN — a horgon kívül, hogy olvasható maradjon.
 *
 * Nem `Promise.all`: lásd a fájl fejlécének első szakaszát. A visszatérés
 * MINDIG az addigi mérleg, bukás esetén is — a hívó ebből építi a `settled`
 * állapotot.
 *
 * ── MELYIK HIBÁNÁL ÁLLUNK MEG, ÉS MELYIKNÉL NEM ──────────────────────
 *
 * A 413 és a 415 EZT A RÉSZLETET minősíti, nem a munkamenetet: azt jelenti,
 * hogy a kliens korlátai elsodródtak a szerverétől (`chunking.ts`,
 * `@/contracts/generated/upload-limits`). A többi részlet ettől még átmehet — megállni itt annyi
 * volna, mint egy elszámolás miatt eldobni a köteg maradékát is. Ezért ezeket
 * FELJEGYEZZÜK és MEGYÜNK TOVÁBB.
 *
 * Minden más — hálózat, 401, 5xx — a KAPCSOLATOT vagy a MUNKAMENETET
 * minősíti: a maradék ugyanabba futna bele, csak lassabban, miközben az
 * ügyfél azt hinné, halad. Ott megállunk.
 */
/**
 * EGY RÉSZLET SORSA. Három kimenet, és a hármat MÁS teszi a hívóval — ezért
 * külön típus, nem `throw`: a „visszautasított" nem hiba, hanem adat.
 */
type ChunkResult =
  | { kind: 'accepted'; stored: number; known: number; newIds: string[] }
  /** A szerver visszaadta (413/415) — ez a részlet vész el, nem a köteg. */
  | { kind: 'refused'; serverMessage: string | null; messageKey: string }
  /** A kapcsolat vagy a munkamenet dőlt el — innen nincs értelme folytatni. */
  | { kind: 'fatal'; failure: UploadFailure };

async function sendChunk(
  chunk: File[],
  signal: AbortSignal,
  grouping?: 'single-document'
): Promise<ChunkResult> {
  try {
    const accepted = await upload<UploadAccepted>('/documents', chunk, signal, grouping);

    // A SZÁMOK A VÁLASZBÓL JÖNNEK, nem a küldött listából. Ha a szerver
    // kevesebbet sorolna fel, mint amennyit küldtünk, a nyugta ne állítson
    // többet annál, amiről tudunk.
    let stored = 0;
    let known = 0;
    const newIds: string[] = [];
    for (const document of accepted.documents) {
      if (document.alreadyHad) {
        known += 1;
      } else {
        stored += 1;
        newIds.push(document.documentId);
      }
    }
    return { kind: 'accepted', stored, known, newIds };
  } catch (caught) {
    if (isChunkShaped(caught)) {
      const { messageKey, serverMessage } = failureFor(caught);
      return { kind: 'refused', serverMessage, messageKey };
    }
    return { kind: 'fatal', failure: failureFor(caught) };
  }
}

async function sendChunks(
  chunks: File[][],
  signalFor: (chunk: readonly File[]) => AbortSignal,
  onProgress: (done: number) => void
): Promise<ChunkOutcome> {
  let stored = 0;
  let known = 0;
  let done = 0;
  const refused: SkippedFile[] = [];
  const newIds: string[] = [];

  for (const chunk of chunks) {
    const result = await sendChunk(chunk, signalFor(chunk));

    if (result.kind === 'fatal') {
      return { stored, known, newIds, refused, failure: result.failure };
    }
    if (result.kind === 'refused') {
      for (const file of chunk) {
        refused.push({
          file,
          reason: 'refused',
          serverMessage: result.serverMessage,
          messageKey: result.messageKey,
        });
      }
    } else {
      stored += result.stored;
      known += result.known;
      newIds.push(...result.newIds);
    }

    // A HALADÁS A VISSZAUTASÍTOTT RÉSZLETTEL IS NŐ: az ügyfél a FÁJLOK
    // elintézettségét látja, nem a sikerüket. A sorsukat a nyugta mondja el.
    done += chunk.length;
    onProgress(done);
  }

  return { stored, known, newIds, refused, failure: null };
}

/**
 * A VÁLASZTOTT TÍPUS RÁÍRÁSA AZ ÚJ BIZONYLATOKRA — egymás után, nem
 * párhuzamosan, ugyanazzal az érvvel, mint a részleteknél. Visszaad: ennyinél
 * NEM sikerült. A hiba okát nem bontjuk szét: mindegyiknél ugyanaz a teendő
 * (a listában utólag megadni), és a 409/422 mondata ott, a sornál jelenik meg.
 *
 * ⚠️ MEGSZAKÍTHATÓ, ÉS IDŐKORLÁTOS. Amíg ez fut, a zár (`inFlight`) zárva és a
 * „Mégsem" látszik — egy beragadt kérés nélküle örökre letiltaná mindkét
 * feltöltési utat. Megszakításkor a hátralévők is a NEM rögzítettek közé
 * számítanak: a bizonylatuk megvan, a típusuk a listában utólag megadható.
 */
async function declareAll(
  ids: readonly string[],
  docType: ClientDocType,
  signal: AbortSignal
): Promise<number> {
  let failed = 0;
  for (const [index, id] of ids.entries()) {
    if (signal.aborted) {
      return failed + (ids.length - index);
    }
    try {
      await declareDocType(id, docType, signal);
    } catch {
      failed += 1;
    }
  }
  return failed;
}

/** A típus-írás időkorlátja: bizonylatonként egy rövid JSON-kérés, bőkezűen mérve. */
const DECLARE_TIMEOUT_BASE_MS = 10_000;
const DECLARE_TIMEOUT_PER_DOC_MS = 5_000;

/** A 413/415 a RÉSZLETRŐL szól, nem a kapcsolatról — lásd a `sendChunks` fejlécét. */
function isChunkShaped(caught: unknown): boolean {
  return (
    caught instanceof ApiError &&
    (caught.status === PAYLOAD_TOO_LARGE || caught.status === UNSUPPORTED_MEDIA_TYPE)
  );
}

export function useUploader(onUploaded: () => void): UploaderState {
  const [outcome, setOutcome] = useState<UploadOutcome>({ kind: 'idle' });
  /**
   * A zár REFERENCIÁN ül, nem az állapoton. A `setOutcome` aszinkron: két
   * gyors koppintás közt a `outcome` MÉG `idle`, tehát egy állapotra épített
   * őr mindkettőt átengedné — és a második feltöltés felülírná az első
   * eredményét.
   */
  const inFlight = useRef(false);
  /** A ÉPPEN futó kérés megszakítója — a `cancel()` és az időkorlát közös fogantyúja. */
  const aborter = useRef<AbortController | null>(null);
  /**
   * A MEGSZAKÍTÁS OKA REFERENCIÁN, nem a jelben. Az `AbortError` magából a
   * jelből nem árulja el, hogy a felhasználó nyomta-e meg a „Mégsem"-et, vagy
   * az időkorlát járt le — a két esetnek pedig MÁS mondat jár: az egyik az ő
   * döntése volt, a másik a mi bukásunk.
   */
  const cancelledByUser = useRef(false);

  const cancel = useCallback(() => {
    cancelledByUser.current = true;
    aborter.current?.abort();
  }, []);

  /**
   * A TÍPUS-ÍRÁS SAJÁT MEGSZAKÍTÓVAL — a `cancel()` az `aborter`-en át ezt éri
   * el, nem az utolsó, már lezárult részletét.
   */
  const declare = useCallback(async (ids: readonly string[], docType: ClientDocType | null) => {
    if (docType === null || ids.length === 0) {
      return 0;
    }
    const controller = new AbortController();
    aborter.current = controller;
    const timer = setTimeout(
      () => controller.abort(),
      DECLARE_TIMEOUT_BASE_MS + ids.length * DECLARE_TIMEOUT_PER_DOC_MS
    );
    try {
      return await declareAll(ids, docType, controller.signal);
    } finally {
      clearTimeout(timer);
    }
  }, []);

  const send = useCallback(
    async (files: File[], docType: ClientDocType | null = null) => {
      if (inFlight.current || files.length === 0) {
        return;
      }
      inFlight.current = true;
      cancelledByUser.current = false;

      // ⚠️ MINDEN A `try`-ON BELÜL VAN, A TERVEZÉST IS BELEÉRTVE. A zárat a
      // `finally` oldja fel; ha bármi a `try` ELŐTT dobna, a zár örökre zárva
      // maradna, és onnantól MINDEN további feltöltés némán visszafordulna —
      // se kérés, se üzenet, se magyarázat. Ez a legrosszabb fajta bukás ebben
      // a projektben: nem történik semmi, és semmi nem szól róla.
      let chunkCount = 0;
      try {
        const { chunks, skipped } = planChunks(files);
        const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
        chunkCount = chunks.length;

        setOutcome({ kind: 'busy', done: 0, total });

        const { stored, known, newIds, refused, failure } = await sendChunks(
          chunks,
          (chunk) => {
            // RÉSZLETENKÉNT ÚJ MEGSZAKÍTÓ. Egy közösen használt jel az első
            // lejárt időkorlát után MINDEN további kérést azonnal eldobna.
            const controller = new AbortController();
            aborter.current = controller;
            const timer = setTimeout(() => controller.abort(), timeoutFor(chunk));
            controller.signal.addEventListener('abort', () => clearTimeout(timer), {
              once: true,
            });
            return controller.signal;
          },
          (done) => setOutcome({ kind: 'busy', done, total })
        );

        // A TÍPUS A RÉSZLEGES SIKERNÉL IS FELMEGY: ami beérkezett, az az
        // ügyfél választásával érkezett be. A `busy` addig marad, hogy a
        // lista frissítése már a típussal együtt mutassa a sort.
        const undeclared = await declare(newIds, docType);

        setOutcome({
          kind: 'settled',
          stored,
          known,
          undeclared,
          skipped: [...skipped, ...refused],
          // A JEL NEM MONDJA MEG, KI SZAKÍTOTTA MEG. Az `AbortError` ugyanaz
          // a lejárt időkorlátnál és a „Mégsem"-nél; a különbséget csak mi
          // tudjuk, és a felhasználónak számít.
          failure:
            failure && cancelledByUser.current && failure.messageKey === TIMED_OUT_KEY
              ? { messageKey: CANCELLED_KEY, serverMessage: null }
              : failure,
        });
      } catch (caught) {
        // A `sendChunks` a HÁLÓZATI hibát maga kezeli; ide az kerül, amire nem
        // számítunk. Enélkül a kimenet ÖRÖKRE `busy` maradna: mindkét belépési
        // pont letiltva, várakozás egy válaszra, ami sosem jön.
        setOutcome({
          kind: 'settled',
          stored: 0,
          known: 0,
          undeclared: 0,
          skipped: [],
          failure: failureFor(caught),
        });
      } finally {
        inFlight.current = false;
        aborter.current = null;
      }

      // A NYUGTA AKKOR IS FRISSÜL, HA A KÖTEG ELBUKOTT — sőt, akkor a
      // legfontosabb: az ügyfélnek látnia kell, mi érkezett meg, mielőtt
      // eldönti, mit küldjön újra.
      //
      // ÉS AKKOR IS, HA A SZÁMLÁLÓNK NULLA. A szerver a kérésen belül
      // EGYESÉVEL, SORBAN dolgozik (`DocumentController.upload`), tehát egy
      // félúton elszálló vagy megszakított kérésből is maradhattak bent
      // bizonylatok, amikről mi sosem kaptunk sort. Az ára egy fölösleges GET;
      // a másik oldalon egy láthatatlanul beérkezett bizonylat áll.
      if (chunkCount > 0) {
        onUploaded();
      }
    },
    [onUploaded, declare]
  );

  /**
   * A SOROZAT KÜLDÉSE — egy kérés, darabolás nélkül.
   *
   * A `send` zárját (`inFlight`) OSZTJA a két út: egyszerre egy feltöltés fut,
   * különben két párhuzamos kérés ugyanarra a bizonylatra a szerver
   * versenyhelyzet-ágára futna — kezelhető, de fölösleges.
   */
  const sendPages = useCallback(
    async (pages: Blob[], docType: ClientDocType | null = null) => {
      if (inFlight.current || pages.length === 0) {
        return;
      }
      inFlight.current = true;
      cancelledByUser.current = false;

      try {
        // A `Blob`-ból `File` lesz, mert a multipart mezőnek NEVE kell — ez az
        // egyetlen emberi fogódzó a szerver naplójában és a nyugtában. A
        // sorszám a NÉVBEN is ott van, nem csak a sorrendben: egy átrendezett
        // vagy hiányos köteg így a naplóból is olvasható.
        const files = pages.map(
          (page, index) =>
            new File([page], `oldal-${index + 1}.jpg`, { type: page.type || 'image/jpeg' })
        );

        setOutcome({ kind: 'busy', done: 0, total: files.length });

        const controller = new AbortController();
        aborter.current = controller;
        const timer = setTimeout(() => controller.abort(), timeoutFor(files));
        controller.signal.addEventListener('abort', () => clearTimeout(timer), { once: true });

        const result = await sendChunk(files, controller.signal, 'single-document');
        clearTimeout(timer);

        const undeclared = await declare(result.kind === 'accepted' ? result.newIds : [], docType);

        // EGY BIZONYLAT KELETKEZETT, nem annyi, ahány oldal — a számok a
        // szerver válaszából jönnek, ami egyelemű listát ad.
        setOutcome({
          kind: 'settled',
          stored: result.kind === 'accepted' ? result.stored : 0,
          known: result.kind === 'accepted' ? result.known : 0,
          undeclared,
          skipped: [],
          failure: failureOf(result, cancelledByUser.current),
        });
      } catch (caught) {
        // Ugyanaz a védelem, mint a `send`-ben: enélkül a kimenet ÖRÖKRE
        // `busy` maradna, és a felület egy sosem érkező válaszra várna.
        setOutcome({
          kind: 'settled',
          stored: 0,
          known: 0,
          undeclared: 0,
          skipped: [],
          failure: failureFor(caught),
        });
      } finally {
        inFlight.current = false;
        aborter.current = null;
      }

      onUploaded();
    },
    [onUploaded, declare]
  );

  const reset = useCallback(() => setOutcome({ kind: 'idle' }), []);

  return { outcome, busy: outcome.kind === 'busy', send, sendPages, cancel, reset };
}
